"use client";

import { Dialog, DialogPanel, DialogTitle } from "@headlessui/react";
import {
  ChartIncreaseIcon,
  DashboardCircleIcon,
  Briefcase01Icon,
  Notification01Icon,
  Message01Icon,
  Mortarboard01Icon,
  HeartAddIcon,
  Cancel01Icon,
} from "hugeicons-react";
import Image from "next/image";
import Link from "next/link";
import callToLogin from "@/assets/imgs/call-to-login.png";
import { useClientTranslations } from "@/lib/translate";
import { routes } from "@/lib/utils/routes";

const ApplyCallLoginPopup = ({
  applyOpenLoginModal,
  onCloseApplyLoginModal,
}: {
  applyOpenLoginModal: boolean;
  onCloseApplyLoginModal: () => void;
}) => {
  const { t: tJobApply } = useClientTranslations("job-apply");

  return (
    <Dialog open={applyOpenLoginModal} onClose={onCloseApplyLoginModal}>
      <div className="fixed inset-0 z-10 w-screen overflow-y-auto bg-black/40 backdrop-blur-sm transition-all duration-300">
        <div className="flex min-h-full items-center justify-center p-4">
          <DialogPanel className="min-w-full sm:min-w-[100px] sm:max-w-screen-md max-h-[80vh] overflow-y-auto mt-7 rounded-2xl bg-white p-0 shadow-2xl relative overflow-hidden animate-fade-in">
            {/* Close Button */}
            <button
              onClick={onCloseApplyLoginModal}
              className="absolute top-4 right-4 z-20 p-2 rounded-full bg-gray-100 hover:bg-gray-200 text-gray-600 hover:text-gray-800 transition-all duration-200 shadow-lg hover:shadow-xl"
              aria-label="Close modal"
            >
              <Cancel01Icon className="w-5 h-5" />
            </button>

            {/* Top Illustration */}
            <div className="flex justify-center mt-12 mb-2 animate-bounce-in">
              <Image
                src={callToLogin}
                width={100}
                height={100}
                alt="Create Profile"
                className="w-24 h-24 object-contain drop-shadow-xl rounded-full border-4 border-white shadow-lg bg-white"
              />
            </div>
            <div className="px-8 pb-8 pt-2 flex flex-col items-center">
              <DialogTitle
                as="h3"
                className="text-3xl font-extrabold text-primary mb-2 text-center drop-shadow-lg"
              >
                {tJobApply("unlockDreamJob")}
              </DialogTitle>
              <p className="mt-2 text-lg text-gray-700 text-center mb-2 font-medium">
                {tJobApply("createProfileEnjoy")}:
              </p>
              <p className="text-base text-primary text-center mb-4 font-semibold bg-primary/10 rounded-lg px-4 py-2 shadow-inner">
                {tJobApply("saveJobCreateAccount")}
              </p>
              <ul className="mb-6 mt-2 space-y-3 text-gray-700 text-base w-full">
                <li className="flex flex-col sm:flex-row items-center sm:items-start gap-2 sm:gap-3 text-center sm:text-left">
                  <span className="inline-flex items-center justify-center w-10 h-10 rounded-full bg-primary/10 text-primary text-lg shadow border border-primary/20 mb-1 sm:mb-0">
                    <ChartIncreaseIcon className="w-5 h-5" />
                  </span>
                  <b>{tJobApply("aiGenTitle")}</b> {tJobApply("aiGenDesc")}
                </li>
                <li className="flex flex-col sm:flex-row items-center sm:items-start gap-2 sm:gap-3 text-center sm:text-left">
                  <span className="inline-flex items-center justify-center w-10 h-10 rounded-full bg-secondary/10 text-secondary text-lg shadow border border-secondary/20 mb-1 sm:mb-0">
                    <DashboardCircleIcon className="w-5 h-5" />
                  </span>
                  <b>{tJobApply("personalizedJobMatches")}</b>{" "}
                  {tJobApply("personalizedJobMatchesDesc")}
                </li>
                <li className="flex flex-col sm:flex-row items-center sm:items-start gap-2 sm:gap-3 text-center sm:text-left">
                  <span className="inline-flex items-center justify-center w-10 h-10 rounded-full bg-green-100 text-green-600 text-lg shadow border border-green-200 mb-1 sm:mb-0">
                    <Briefcase01Icon className="w-5 h-5" />
                  </span>
                  <b>{tJobApply("oneClickJobApplications")}</b>{" "}
                  {tJobApply("oneClickJobApplicationsDesc")}
                </li>
                <li className="flex flex-col sm:flex-row items-center sm:items-start gap-2 sm:gap-3 text-center sm:text-left">
                  <span className="inline-flex items-center justify-center w-10 h-10 rounded-full bg-blue-100 text-blue-600 text-lg shadow border border-blue-200 mb-1 sm:mb-0">
                    <Notification01Icon className="w-5 h-5" />
                  </span>
                  <b>{tJobApply("realTimeApplicationUpdates")}</b>{" "}
                  {tJobApply("realTimeApplicationUpdatesDesc")}
                </li>
                <li className="flex flex-col sm:flex-row items-center sm:items-start gap-2 sm:gap-3 text-center sm:text-left">
                  <span className="inline-flex items-center justify-center w-10 h-10 rounded-full bg-pink-100 text-pink-600 text-lg shadow border border-pink-200 mb-1 sm:mb-0">
                    <Message01Icon className="w-5 h-5" />
                  </span>
                  <b>{tJobApply("chatInterviewDirectly")}</b>{" "}
                  {tJobApply("chatInterviewDirectlyDesc")}
                </li>
                <li className="flex flex-col sm:flex-row items-center sm:items-start gap-2 sm:gap-3 text-center sm:text-left">
                  <span className="inline-flex items-center justify-center w-10 h-10 rounded-full bg-yellow-100 text-yellow-600 text-lg shadow border border-yellow-200 mb-1 sm:mb-0">
                    <Mortarboard01Icon className="w-5 h-5" />
                  </span>
                  <b>{tJobApply("personalizedCourseRecommendations")}</b>{" "}
                  {tJobApply("personalizedCourseRecommendationsDesc")}
                </li>
                <li className="flex flex-col sm:flex-row items-center sm:items-start gap-2 sm:gap-3 text-center sm:text-left">
                  <span className="inline-flex items-center justify-center w-10 h-10 rounded-full bg-red-100 text-red-600 text-lg shadow border border-red-200 mb-1 sm:mb-0">
                    <HeartAddIcon className="w-5 h-5" />
                  </span>
                  <b>{tJobApply("saveFavoriteJobs")}</b>{" "}
                  {tJobApply("saveFavoriteJobsDesc")}
                </li>
              </ul>
              <Link
                href={routes.auth.login}
                className="inline-flex items-center gap-2 rounded-lg bg-primary px-7 py-3 text-lg font-bold text-white shadow-inner shadow-white/10 hover:bg-primaryDark focus:outline-none transition-all duration-200 animate-pulse hover:scale-105"
              >
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  className="w-6 h-6 text-white"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M5 13l4 4L19 7"
                  />
                </svg>
                {tJobApply("loginToGenSuggestionCallToAction")}
              </Link>
            </div>
          </DialogPanel>
        </div>
      </div>
    </Dialog>
  );
};

ApplyCallLoginPopup.displayName = "ApplyCallLoginPopup";
export default ApplyCallLoginPopup;
